import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { createResource, type ApiError } from '../core'
import type { CollectionAssetDto, CollectionUploadDto } from './collections.schemas'
import { collectionsService } from './collections.service'

/** TanStack Query for collections: `useAllCollections({ accountId })` is every folder of one account. */
export const collections = createResource('collections', collectionsService)

export const collectionsKeys = collections.keys
export const collectionsQueries = collections.queries
export const useCollections = collections.useList
export const useAllCollections = collections.useAll
export const useCollection = collections.useOne
export const useCreateCollection = collections.useCreate
export const useUpdateCollection = collections.useUpdate
export const useDeleteCollection = collections.useRemove

const assetsKey = (collectionId: string) => [...collectionsKeys.detail(collectionId), 'assets'] as const

export const collectionAssetsQuery = (collectionId: string) => queryOptions({ queryKey: assetsKey(collectionId), queryFn: () => collectionsService.listAssets(collectionId) })

/** `useCollectionAssets(id)`: the template files kept in a collection. Pass `undefined` to hold off. */
export function useCollectionAssets(collectionId: string | undefined) {
  return useQuery({ ...collectionAssetsQuery(collectionId ?? ''), enabled: Boolean(collectionId) })
}

/** `useUploadCollectionAssets().mutateAsync({ collectionId, files })`: the files that were kept join the list at once. Never retried by itself. */
export function useUploadCollectionAssets() {
  const queryClient = useQueryClient()
  return useMutation<CollectionUploadDto, ApiError, { collectionId: string; files: readonly File[] }>({
    mutationFn: ({ collectionId, files }) => collectionsService.uploadAssets(collectionId, files),
    onSuccess: (result, { collectionId }) => {
      queryClient.setQueryData<CollectionAssetDto[]>(assetsKey(collectionId), (list) => [...(list ?? []), ...result.assets])
    },
  })
}

/** `useDeleteCollectionAsset().mutate({ collectionId, assetId })`: the template leaves the list at once and comes back if the server says no. */
export function useDeleteCollectionAsset() {
  const queryClient = useQueryClient()
  return useMutation<void, ApiError, { collectionId: string; assetId: string }, { before: CollectionAssetDto[] | undefined }>({
    mutationFn: ({ collectionId, assetId }) => collectionsService.removeAsset(collectionId, assetId),
    onMutate: async ({ collectionId, assetId }) => {
      await queryClient.cancelQueries({ queryKey: assetsKey(collectionId) })
      const before = queryClient.getQueryData<CollectionAssetDto[]>(assetsKey(collectionId))
      queryClient.setQueryData<CollectionAssetDto[]>(assetsKey(collectionId), (list) => list?.filter((asset) => asset.id !== assetId))
      return { before }
    },
    onError: (_error, { collectionId }, context) => {
      if (context?.before) queryClient.setQueryData(assetsKey(collectionId), context.before)
    },
  })
}

/** `useCollectionFile(assetId, index)`: one picture of a kept file (its `fileUrl` list, from 0), loading, or why it could not be had. Pass `undefined` to hold off. Kept for a few minutes. */
export function useCollectionFile(assetId: string | undefined, index = 0) {
  return useQuery({
    queryKey: [...collectionsKeys.root, 'file', assetId, index],
    queryFn: () => collectionsService.downloadFile(assetId!, index),
    enabled: Boolean(assetId),
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
